/**
 * Servicio: MemberRoleAssignments (multi-cargo por persona)
 *
 * Colección: /memberRoles
 *
 * Reglas de negocio implementadas aquí:
 * - Una persona puede tener varios cargos activos a la vez (director de un
 *   club + tesorero de otro, etc).
 * - No permitimos DOS activos con la misma tripleta `(clubId, section, role)`.
 * - Al desactivar una asignación se conserva el registro (`status='inactive'`,
 *   `endDate` set) — nunca borrar por defecto.
 * - Cuando este servicio activa/desactiva una asignación en un club, también
 *   actualiza el campo legacy `member.role` / `member.staffSection` para
 *   mantener compatibilidad con el código existente. El "cargo principal"
 *   sincronizado con `member` es el de mayor jerarquía (según
 *   `MEMBER_ROLE_LEVELS`).
 *
 * Feature flag: /appConfig/features.memberRolesEnabled
 * El servicio funciona con o sin flag; el flag sólo controla si la UI expone
 * la sección "Cargos" y si las guardas RBAC dinámicas reemplazan las estáticas.
 */

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
  Timestamp,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import {
  isRoleAllowedInSection,
  roleSectionRestrictionMessage,
} from "@/lib/constants/roles";
import type {
  ClubType,
  Member,
  MemberRole,
  MemberRoleAssignment,
  MemberRoleAssignmentStatus,
} from "@/types";

const COLLECTION = "memberRoles";
const MEMBERS_COLLECTION = "members";

/** Jerarquía para elegir el cargo "principal" que se copia al Member legacy. */
const ROLE_RANK: Record<MemberRole, number> = {
  director: 1,
  subdirector: 2,
  secretario: 3,
  tesorero: 4,
  anciano: 5,
  consejero: 6,
  instructor: 7,
  member: 99,
};

// ==================== INTERNAL HELPERS ====================

function docToAssignment(
  id: string,
  data: Record<string, unknown>,
): MemberRoleAssignment {
  return { id, ...(data as object) } as MemberRoleAssignment;
}

async function fetchMember(memberId: string): Promise<Member | null> {
  const snap = await getDoc(doc(db, MEMBERS_COLLECTION, memberId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as Member;
}

/**
 * Recalcula el cargo "principal" (el de mayor jerarquía entre las
 * asignaciones activas del member) y devuelve el par
 * `{ role, staffSection }` que debe escribirse en el doc `Member`.
 *
 * Si no queda ninguna asignación activa, se cae a `role='member'` y se
 * limpia `staffSection`.
 */
function pickPrincipal(active: MemberRoleAssignment[]): {
  role: MemberRole;
  staffSection?: ClubType;
} {
  if (active.length === 0) {
    return { role: "member", staffSection: undefined };
  }
  const sorted = [...active].sort(
    (a, b) => (ROLE_RANK[a.role] ?? 50) - (ROLE_RANK[b.role] ?? 50),
  );
  const top = sorted[0];
  return { role: top.role, staffSection: top.section };
}

async function fetchActiveByMember(
  memberId: string,
): Promise<MemberRoleAssignment[]> {
  const q = query(
    collection(db, COLLECTION),
    where("memberId", "==", memberId),
    where("status", "==", "active"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => docToAssignment(d.id, d.data()));
}

// ==================== QUERIES ====================

/** Todas las asignaciones activas de una persona (a través de sus members). */
export async function getActiveAssignmentsByPerson(
  personId: string,
): Promise<MemberRoleAssignment[]> {
  const q = query(
    collection(db, COLLECTION),
    where("personId", "==", personId),
    where("status", "==", "active"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => docToAssignment(d.id, d.data()));
}

/** Todas las asignaciones activas de un club (útil para vista de "Equipo"). */
export async function getActiveAssignmentsByClub(
  clubId: string,
): Promise<MemberRoleAssignment[]> {
  const q = query(
    collection(db, COLLECTION),
    where("clubId", "==", clubId),
    where("status", "==", "active"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => docToAssignment(d.id, d.data()));
}

/** Asignaciones activas por sección específica dentro de un club. */
export async function getActiveAssignmentsBySection(
  clubId: string,
  section: ClubType,
): Promise<MemberRoleAssignment[]> {
  const q = query(
    collection(db, COLLECTION),
    where("clubId", "==", clubId),
    where("section", "==", section),
    where("status", "==", "active"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => docToAssignment(d.id, d.data()));
}

/** Historial completo (activo + inactivo) de un member. */
export async function getAllAssignmentsByMember(
  memberId: string,
): Promise<MemberRoleAssignment[]> {
  const q = query(
    collection(db, COLLECTION),
    where("memberId", "==", memberId),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => docToAssignment(d.id, d.data()));
}

// ==================== COMMANDS ====================

export interface AssignRoleInput {
  memberId: string;
  personId: string;
  clubId: string;
  section: ClubType;
  role: MemberRole;
  assignedBy: string;
  assignedByName?: string;
  personName?: string;
  notes?: string;
  /** Fecha de inicio. Por defecto ahora. */
  startDate?: Date;
}

/**
 * Crea una nueva asignación de cargo.
 *
 * - Valida que no exista otra activa con la misma tripleta.
 * - Si `role === 'member'` se rechaza (ese cargo lo representa `Member`).
 * - Sincroniza el campo legacy `member.role/staffSection` con el nuevo
 *   principal.
 *
 * Devuelve el id del documento creado.
 */
export async function assignRole(input: AssignRoleInput): Promise<string> {
  if (input.role === "member") {
    throw new Error(
      "El cargo 'member' no se representa en /memberRoles; usa el Member base.",
    );
  }
  if (!isRoleAllowedInSection(input.role, input.section)) {
    throw new Error(
      roleSectionRestrictionMessage(input.role, input.section) ??
        "Combinación rol/sección no permitida.",
    );
  }

  const member = await fetchMember(input.memberId);
  if (!member) throw new Error(`Member ${input.memberId} no existe`);
  if (member.clubId !== input.clubId) {
    throw new Error(
      "El member no pertenece al clubId indicado — asignación rechazada",
    );
  }
  if (member.personId !== input.personId) {
    throw new Error(
      "El member no corresponde al personId indicado — asignación rechazada",
    );
  }

  const activeSame = await getDocs(
    query(
      collection(db, COLLECTION),
      where("memberId", "==", input.memberId),
      where("clubId", "==", input.clubId),
      where("section", "==", input.section),
      where("role", "==", input.role),
      where("status", "==", "active"),
    ),
  );
  if (!activeSame.empty) {
    throw new Error(
      `Ya existe una asignación activa (${input.role} / ${input.section}) para este miembro.`,
    );
  }

  const now = serverTimestamp();
  const start = input.startDate
    ? Timestamp.fromDate(input.startDate)
    : Timestamp.now();

  const ref = await addDoc(collection(db, COLLECTION), {
    memberId: input.memberId,
    personId: input.personId,
    clubId: input.clubId,
    section: input.section,
    role: input.role,
    status: "active" satisfies MemberRoleAssignmentStatus,
    startDate: start,
    assignedBy: input.assignedBy,
    assignedByName: input.assignedByName ?? null,
    personName: input.personName ?? null,
    notes: input.notes ?? null,
    createdAt: now,
    createdBy: input.assignedBy,
    updatedAt: now,
    updatedBy: input.assignedBy,
  });

  // Sync legacy fields dentro de una transacción para asegurar consistencia
  // strong (evita race entre client y CF `onMemberRolesWrite`).
  await syncMemberInTransaction(input.memberId, input.assignedBy);

  return ref.id;
}

/**
 * Marca una asignación como inactiva (fin de cargo). No la borra.
 * Sincroniza el `member.role/staffSection` con el nuevo cargo principal.
 *
 * El client-side sync es best-effort (optimistic update para UX responsiva).
 * La CF `onMemberRolesWrite` corre después con transacción y garantiza
 * consistencia strong final.
 */
export async function deactivateAssignment(
  assignmentId: string,
  actor: { userId: string; userName?: string; reason?: string },
): Promise<void> {
  await runTransaction(db, async (tx) => {
    const ref = doc(db, COLLECTION, assignmentId);
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error("Asignación no existe");
    const data = snap.data() as MemberRoleAssignment;
    if (data.status !== "active") return; // idempotente

    tx.update(ref, {
      status: "inactive",
      endDate: Timestamp.now(),
      notes: actor.reason ?? data.notes ?? null,
      updatedAt: serverTimestamp(),
      updatedBy: actor.userId,
    });
  });

  // Re-sync tras cerrar la transacción (optimistic — la CF backend hace
  // la sincronización definitiva con transacción a prueba de races).
  const closed = await getDoc(doc(db, COLLECTION, assignmentId));
  if (!closed.exists()) return;
  const { memberId } = closed.data() as MemberRoleAssignment;
  const active = await fetchActiveByMember(memberId);
  const principal = pickPrincipal(active);
  await updateDoc(doc(db, MEMBERS_COLLECTION, memberId), {
    role: principal.role,
    staffSection: principal.staffSection ?? null,
    updatedAt: serverTimestamp(),
    updatedBy: actor.userId,
  });
}

/**
 * Actualiza cargo/sección de una asignación existente (corrección puntual).
 * Rechaza si el cambio genera duplicado activo con misma tripleta.
 */
export async function updateAssignment(
  assignmentId: string,
  patch: { role?: MemberRole; section?: ClubType; notes?: string },
  actor: { userId: string; userName?: string },
): Promise<void> {
  if (patch.role === "member") {
    throw new Error("El cargo 'member' no puede asignarse aquí.");
  }
  await runTransaction(db, async (tx) => {
    const ref = doc(db, COLLECTION, assignmentId);
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error("Asignación no existe");
    const data = snap.data() as MemberRoleAssignment;

    const nextRole = patch.role ?? data.role;
    const nextSection = patch.section ?? data.section;
    if (nextRole === data.role && nextSection === data.section) return;

    if (!isRoleAllowedInSection(nextRole, nextSection)) {
      throw new Error(
        roleSectionRestrictionMessage(nextRole, nextSection) ??
          "Combinación rol/sección no permitida.",
      );
    }

    // Verificar duplicado (solo si la asignación está activa)
    if (data.status === "active") {
      const dupSnap = await getDocs(
        query(
          collection(db, COLLECTION),
          where("memberId", "==", data.memberId),
          where("clubId", "==", data.clubId),
          where("section", "==", nextSection),
          where("role", "==", nextRole),
          where("status", "==", "active"),
        ),
      );
      const conflict = dupSnap.docs.find((d) => d.id !== assignmentId);
      if (conflict) {
        throw new Error(
          `Ya existe un cargo activo (${nextRole} / ${nextSection}) para este miembro.`,
        );
      }
    }

    tx.update(ref, {
      role: nextRole,
      section: nextSection,
      notes: patch.notes ?? data.notes ?? null,
      updatedAt: serverTimestamp(),
      updatedBy: actor.userId,
    });
  });

  // Re-sync legacy fields optimista (la CF garantiza el estado final).
  const closed = await getDoc(doc(db, COLLECTION, assignmentId));
  if (!closed.exists()) return;
  const { memberId } = closed.data() as MemberRoleAssignment;
  await syncMemberInTransaction(memberId, actor.userId);
}

/**
 * Elimina definitivamente una asignación (solo para correcciones de errores).
 * A diferencia de `deactivateAssignment`, no queda historial.
 */
export async function hardDeleteAssignment(
  assignmentId: string,
  actor: { userId: string },
): Promise<void> {
  const ref = doc(db, COLLECTION, assignmentId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const { memberId } = snap.data() as MemberRoleAssignment;
  await deleteDoc(ref);
  await syncMemberInTransaction(memberId, actor.userId);
}

/** Optimistic re-sync del member desde el cliente. La CF backend garantiza consistencia final. */
async function syncMemberInTransaction(
  memberId: string,
  actor: string,
): Promise<void> {
  const active = await fetchActiveByMember(memberId);
  const principal = pickPrincipal(active);
  await updateDoc(doc(db, MEMBERS_COLLECTION, memberId), {
    role: principal.role,
    staffSection: principal.staffSection ?? null,
    updatedAt: serverTimestamp(),
    updatedBy: actor,
  });
}

/**
 * Utilidad para reconstruir el estado legacy de un member a partir de sus
 * asignaciones activas. Útil para reparación puntual y para el script de
 * migración.
 */
export async function syncMemberFromAssignments(
  memberId: string,
  actor: string,
): Promise<{ role: MemberRole; staffSection?: ClubType }> {
  const active = await fetchActiveByMember(memberId);
  const principal = pickPrincipal(active);
  await updateDoc(doc(db, MEMBERS_COLLECTION, memberId), {
    role: principal.role,
    staffSection: principal.staffSection ?? null,
    updatedAt: serverTimestamp(),
    updatedBy: actor,
  });
  return principal;
}
